import { FC } from 'react';
import { WidgetCell, WidgetProps } from '../WidgetCell';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Stack, Typography } from '@mui/material';
import { CalendarTodayOutlined, LoopOutlined } from '@mui/icons-material';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { getNightOrDayPalette } from '../../../../../utils/getNightOrDayPalette';
import slugify from 'slugify';
import { widgetDefinitions } from '../widgetDefinitions';
import { ActivityDurationOccurenceUnitType } from '../../../../../gql/graphql';

type Duration = NonNullable<WidgetProps['widget']['duration']>;

export const DurationCell: FC<WidgetProps> = ({ activityId, activityName, widget: { duration } }) => {
	const intl = useIntl();
	const { setDrawerWithoutPrevParams } = useDrawerContext();

	return (
		<WidgetCell
			data-activity-widget-name={slugify(activityName, { lower: true })}
			data-activity-widget-type={widgetDefinitions.duration.type}
			showEditOnHover
			tooltipText={intl.formatMessage({ id: 'journey.detail.widget.edit' })}
			sx={{ verticalAlign: 'middle' }}
			handleEditAction={() => setDrawerWithoutPrevParams('editDuration', { activityId })}
		>
			{duration && <DurationVariant duration={duration} />}
		</WidgetCell>
	);
};

type DurationVariantProps = Readonly<{ duration: Duration; smallerFontSize?: boolean }>;

export const DurationVariant: FC<DurationVariantProps> = ({ duration, smallerFontSize = false }) => {
	const fontSize = smallerFontSize ? 'body2' : 'body1';

	return (
		<Stack direction={'row'} flexWrap={'nowrap'} gap={1} alignItems={'center'}>
			{duration.type === 'time_limited' && (
				<Stack direction={'row'} gap={1}>
					<CalendarTodayOutlined color={'secondary'} sx={{ width: 18, height: 18 }} />
					{duration.start && (
						<Typography variant={fontSize} sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][600] }}>
							<FormattedDate value={new Date(duration.start)} year={'numeric'} month={'short'} day={'2-digit'} />
							{duration.end && duration.end !== duration.start && (
								// eslint-disable-next-line formatjs/no-literal-string-in-jsx
								<>
									&nbsp;-&nbsp;
									<FormattedDate value={new Date(duration.end)} year={'numeric'} month={'short'} day={'2-digit'} />
								</>
							)}
						</Typography>
					)}
				</Stack>
			)}
			{duration.type === 'continuous' && (
				<>
					<LoopOutlined color={'secondary'} />
					<Typography variant={fontSize} sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][600] }}>
						<FormattedMessage id={'journey.detail.widget.duration.continuous'} />
					</Typography>
				</>
			)}
			{duration.type === 'one_time' && (
				<Typography variant={fontSize} sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][600] }}>
					<FormattedMessage id={'journey.detail.widget.duration.one-time'} />
				</Typography>
			)}
			{duration.type === 'occurence' && (
				<Typography
					variant={fontSize}
					sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][800], textTransform: 'lowercase' }}
				>
					{formatOrdinals(duration.occurence ?? 0)}&nbsp;
					<OccurenceUnit unit={duration.unit ?? 'year'} />
				</Typography>
			)}
		</Stack>
	);
};

// Using recommended approach from MDN
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules#formatting_text_using_the_returned_tag

const suffixes = new Map([
	['one', 'st'],
	['two', 'nd'],
	['few', 'rd'],
	['other', 'th'],
]);

const formatOrdinals = (n: number) => {
	const rule = new Intl.PluralRules(undefined, { type: 'ordinal' }).select(n);
	const suffix = suffixes.get(rule);
	return `${n}${suffix}`;
};

const OccurenceUnit: FC<Readonly<{ unit: ActivityDurationOccurenceUnitType }>> = ({ unit }) => {
	if (unit === 'day') {
		return <FormattedMessage id={'duration.unit.day'} />;
	}
	if (unit === 'hour') {
		return <FormattedMessage id={'duration.unit.hour'} />;
	}
	if (unit === 'month') {
		return <FormattedMessage id={'duration.unit.month'} />;
	}
	return <FormattedMessage id={'duration.unit.year'} />;
};
